import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Search, X, Phone, Mail, Plus, RefreshCw } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const Suppliers = () => {
  const [suppliers,         setSuppliers]         = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [cities,            setCities]            = useState([]);
  const [states,            setStates]            = useState([]);
  const [countries,         setCountries]         = useState([]);
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState('');
  const [searchTerm,        setSearchTerm]        = useState('');
  const [showForm,          setShowForm]          = useState(false);
  const [editingId,         setEditingId]         = useState(null);

  const emptyForm = {
    supplierid: null, suppliercode: '', suppliername: '', contactperson: '',
    phone: '', email: '', mobile: '', website: '',
    address: '', cityid: '', stateid: '', countryid: '', pincode: '',
    gstin: '', pan: '', tan: '', taxrate: 0,
    creditdays: 0, creditlimit: 0, paymentterms: '',
    bankname: '', accountnumber: '', ifsccode: '', branch: '',
    suppliertype: '', category: '', remarks: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadSuppliers(); loadCountries(); }, []);

  useEffect(() => {
    if (!searchTerm.trim()) { setFilteredSuppliers(suppliers); return; }
    const q = searchTerm.toLowerCase();
    setFilteredSuppliers(suppliers.filter(s =>
      (s.suppliercode || '').toLowerCase().includes(q) ||
      (s.suppliername || '').toLowerCase().includes(q) ||
      (s.phone        || '').includes(searchTerm) ||
      (s.email        || '').toLowerCase().includes(q) ||
      (s.gstin        || '').toLowerCase().includes(q)
    ));
  }, [searchTerm, suppliers]);

  useEffect(() => {
    if (formData.countryid) loadStates(formData.countryid);
    else { setStates([]); setCities([]); }
  }, [formData.countryid]);

  useEffect(() => {
    if (formData.stateid) loadCities(formData.stateid);
    else setCities([]);
  }, [formData.stateid]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/Suppliers.php');
      if (res.data.success) { const list = res.data.data.suppliers || []; setSuppliers(list); setFilteredSuppliers(list); }
    } catch { setError('Failed to load suppliers'); }
    finally { setLoading(false); }
  };

  const loadCountries = async () => {
    try {
      const res = await apiClient.get('/Countries.php', { params: { active: 1 } });
      if (res.data.success) { const raw = res.data.data; setCountries(Array.isArray(raw) ? raw : (raw.countries || [])); }
    } catch (e) { console.error(e); }
  };

  const loadStates = async (countryId) => {
    try {
      const res = await apiClient.get('/States.php', { params: { countryid: countryId, active: 1 } });
      if (res.data.success) setStates(res.data.data || res.data.data?.states || []);
    } catch (e) { console.error(e); }
  };

  const loadCities = async (stateId) => {
    try {
      const res = await apiClient.get('/Cities.php', { params: { stateid: stateId, active: 1 } });
      if (res.data.success) setCities(res.data.data || res.data.data?.cities || []);
    } catch (e) { console.error(e); }
  };

  const generateSupplierCode = async () => {
    try {
      const res = await apiClient.get('/Suppliers.php?generate_code');
      if (res.data.success) setFormData(prev => ({ ...prev, suppliercode: res.data.data.suppliercode }));
    } catch (e) { console.error(e); }
  };

  const handleChange       = (e) => { const { name, value }  = e.target; setFormData(p => ({ ...p, [name]: value })); };
  const handleNumberChange = (e) => { const { name, value }  = e.target; setFormData(p => ({ ...p, [name]: value === '' ? 0 : parseFloat(value) })); };
  const handleCountryChange = (e) => setFormData(p => ({ ...p, countryid: e.target.value, stateid: '', cityid: '' }));
  const handleStateChange   = (e) => setFormData(p => ({ ...p, stateid: e.target.value, cityid: '' }));

  const validateForm = () => {
    if (!formData.suppliercode.trim()) { setError('Supplier code is required'); return false; }
    if (!formData.suppliername.trim()) { setError('Supplier name is required'); return false; }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setError('Invalid email format'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true); setError('');
      const res = editingId ? await apiClient.put('/Suppliers.php', formData) : await apiClient.post('/Suppliers.php', formData);
      if (res.data.success) { await loadSuppliers(); handleClear(); setShowForm(false); }
      else setError(res.data.message || 'Failed to save supplier');
    } catch (err) { setError('Failed to save supplier: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleEdit = async (supplier) => {
    setFormData({
      supplierid: supplier.supplierid, suppliercode: supplier.suppliercode || '', suppliername: supplier.suppliername || '',
      contactperson: supplier.contactperson || '', phone: supplier.phone || '', email: supplier.email || '',
      mobile: supplier.mobile || '', website: supplier.website || '', address: supplier.address || '',
      cityid: supplier.cityid || '', stateid: supplier.stateid || '', countryid: supplier.countryid || '',
      pincode: supplier.pincode || '', gstin: supplier.gstin || '', pan: supplier.pan || '', tan: supplier.tan || '',
      taxrate: parseFloat(supplier.taxrate) || 0, creditdays: parseInt(supplier.creditdays) || 0,
      creditlimit: parseFloat(supplier.creditlimit) || 0, paymentterms: supplier.paymentterms || '',
      bankname: supplier.bankname || '', accountnumber: supplier.accountnumber || '',
      ifsccode: supplier.ifsccode || '', branch: supplier.branch || '',
      suppliertype: supplier.suppliertype || '', category: supplier.category || '', remarks: supplier.remarks || '',
    });
    if (supplier.countryid) await loadStates(supplier.countryid);
    if (supplier.stateid)   await loadCities(supplier.stateid);
    setEditingId(supplier.supplierid); setShowForm(true); setError('');
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      setLoading(true);
      const res = await apiClient.delete('/Suppliers.php', { data: { supplierid: supplierId } });
      if (res.data.success) await loadSuppliers();
      else setError(res.data.message || 'Failed to delete supplier');
    } catch (err) { setError('Failed to delete supplier: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleClear  = () => { setFormData(emptyForm); setEditingId(null); setError(''); setStates([]); setCities([]); };
  const handleNew    = async () => { handleClear(); await generateSupplierCode(); setShowForm(true); };
  const handleCancel = () => { handleClear(); setShowForm(false); };

  // shared styles
  const inputCls   = `w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed`;
  const selectCls  = (disabled) => `w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`;
  const labelCls   = 'block mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide';

  const SectionLabel = ({ emoji, title }) => (
    <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide
      px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
      style={{ borderLeftColor: NAVY }}>
      {emoji && <span>{emoji}</span>} {title}
    </div>
  );

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 text-xl">
            🏢
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Supplier Management</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage supplier information and records</p>
          </div>
        </div>
        {!showForm && (
          <button onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
            style={{ color: NAVY }}>
            <Plus size={17} /> New Supplier
          </button>
        )}
      </div>

      <div className="flex-1 p-5">

        {/* ── ERROR BANNER ── */}
        {error && (
          <div className="flex items-center gap-2.5 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-semibold">
            <span>⚠️</span>
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')}
              className="text-red-500 hover:text-red-700 bg-transparent border-0 cursor-pointer text-lg leading-none">×</button>
          </div>
        )}

        {/* ── FORM ── */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-5 overflow-hidden">
            {/* Form header */}
            <div className="px-6 py-4 border-b border-gray-100"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="text-[17px] font-bold m-0" style={{ color: '#fff' }}>
                {editingId ? '✏️ Edit Supplier' : '➕ New Supplier'}
              </h3>
            </div>

            <div className="p-6">

              {/* ── Basic Info ── */}
              <SectionLabel emoji="📋" title="Basic Information" />
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <label className={labelCls}>Supplier Code <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <input type="text" name="suppliercode" value={formData.suppliercode}
                      onChange={handleChange} disabled={loading} placeholder="SUP0001"
                      className={inputCls + ' flex-1'} />
                    {!editingId && (
                      <button type="button" onClick={generateSupplierCode} disabled={loading}
                        className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-60">
                        <RefreshCw size={12} /> Gen
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-span-1">
                  <label className={labelCls}>Supplier Name <span className="text-red-500">*</span></label>
                  <input type="text" name="suppliername" value={formData.suppliername}
                    onChange={handleChange} disabled={loading} placeholder="Supplier Name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Supplier Type</label>
                  <select name="suppliertype" value={formData.suppliertype} onChange={handleChange} disabled={loading} className={selectCls(loading)}>
                    <option value="">-- Select Type --</option>
                    {['VENDOR','CONTRACTOR','SERVICE_PROVIDER','FREIGHT_FORWARDER','TRANSPORT_COMPANY','OTHER'].map(t => (
                      <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Category</label>
                  <input type="text" name="category" value={formData.category} onChange={handleChange} disabled={loading} placeholder="Category" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Contact Person</label>
                  <input type="text" name="contactperson" value={formData.contactperson} onChange={handleChange} disabled={loading} placeholder="Contact Person Name" className={inputCls} />
                </div>
              </div>

              {/* ── Contact ── */}
              <SectionLabel emoji="📞" title="Contact Information" />
              <div className="grid grid-cols-4 gap-4 mb-5">
                <div><label className={labelCls}>Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={loading} placeholder="+91-XXXXXXXXXX" className={inputCls} /></div>
                <div><label className={labelCls}>Mobile</label><input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} disabled={loading} placeholder="+91-XXXXXXXXXX" className={inputCls} /></div>
                <div><label className={labelCls}>Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} disabled={loading} placeholder="supplier@example.com" className={inputCls} /></div>
                <div><label className={labelCls}>Website</label><input type="url" name="website" value={formData.website} onChange={handleChange} disabled={loading} placeholder="https://www.example.com" className={inputCls} /></div>
              </div>

              {/* ── Address ── */}
              <SectionLabel emoji="📍" title="Address Information" />
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div className="col-span-2">
                  <label className={labelCls}>Address</label>
                  <textarea name="address" value={formData.address} onChange={handleChange} disabled={loading} rows={2} placeholder="Street Address"
                    className={`${inputCls} resize-y font-[inherit]`} />
                </div>
                <div>
                  <label className={labelCls}>Pin Code</label>
                  <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} disabled={loading} placeholder="123456" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Country</label>
                  <select name="countryid" value={formData.countryid} onChange={handleCountryChange} disabled={loading} className={selectCls(loading)}>
                    <option value="">-- Select Country --</option>
                    {countries.map(c => <option key={c.countryid} value={c.countryid}>{c.countryname}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>State</label>
                  <select name="stateid" value={formData.stateid} onChange={handleStateChange} disabled={loading || !formData.countryid} className={selectCls(loading || !formData.countryid)}>
                    <option value="">-- Select State --</option>
                    {states.map(s => <option key={s.stateid} value={s.stateid}>{s.statename}</option>)}
                  </select>
                  {!formData.countryid && <p className="text-[11px] text-gray-400 mt-1">Select country first</p>}
                </div>
                <div>
                  <label className={labelCls}>City</label>
                  <select name="cityid" value={formData.cityid} onChange={handleChange} disabled={loading || !formData.stateid} className={selectCls(loading || !formData.stateid)}>
                    <option value="">-- Select City --</option>
                    {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                  </select>
                  {!formData.stateid && <p className="text-[11px] text-gray-400 mt-1">Select state first</p>}
                </div>
              </div>

              {/* ── Tax ── */}
              <SectionLabel emoji="📄" title="Tax & Legal Information" />
              <div className="grid grid-cols-4 gap-4 mb-5">
                <div><label className={labelCls}>Tax No</label><input type="text" name="gstin" value={formData.gstin} onChange={handleChange} disabled={loading} placeholder="15 characters" maxLength="15" className={inputCls} /></div>
                <div><label className={labelCls}>PAN</label><input type="text" name="pan" value={formData.pan} onChange={handleChange} disabled={loading} placeholder="10 characters" maxLength="10" className={inputCls + ' uppercase'} /></div>
                <div><label className={labelCls}>TAN</label><input type="text" name="tan" value={formData.tan} onChange={handleChange} disabled={loading} placeholder="TAN Number" maxLength="10" className={inputCls} /></div>
                <div><label className={labelCls}>Tax Rate (%)</label><input type="number" name="taxrate" value={formData.taxrate} onChange={handleNumberChange} disabled={loading} step="0.01" min="0" max="100" placeholder="0.00" className={inputCls + ' text-right'} /></div>
              </div>

              {/* ── Payment Terms ── */}
              <SectionLabel emoji="💳" title="Payment Terms" />
              <div className="grid grid-cols-4 gap-4 mb-5">
                <div><label className={labelCls}>Credit Days</label><input type="number" name="creditdays" value={formData.creditdays} onChange={handleNumberChange} disabled={loading} min="0" placeholder="0" className={inputCls + ' text-right'} /></div>
                <div><label className={labelCls}>Credit Limit</label><input type="number" name="creditlimit" value={formData.creditlimit} onChange={handleNumberChange} disabled={loading} step="0.01" min="0" placeholder="0.00" className={inputCls + ' text-right'} /></div>
                <div className="col-span-2"><label className={labelCls}>Payment Terms</label><input type="text" name="paymentterms" value={formData.paymentterms} onChange={handleChange} disabled={loading} placeholder="Payment terms details" className={inputCls} /></div>
              </div>

              {/* ── Bank ── */}
              <SectionLabel emoji="🏦" title="Bank Details" />
              <div className="grid grid-cols-4 gap-4 mb-5">
                <div><label className={labelCls}>Bank Name</label><input type="text" name="bankname" value={formData.bankname} onChange={handleChange} disabled={loading} placeholder="Bank Name" className={inputCls} /></div>
                <div><label className={labelCls}>Account Number</label><input type="text" name="accountnumber" value={formData.accountnumber} onChange={handleChange} disabled={loading} placeholder="Account Number" className={inputCls} /></div>
                <div><label className={labelCls}>IFSC Code</label><input type="text" name="ifsccode" value={formData.ifsccode} onChange={handleChange} disabled={loading} placeholder="IFSC Code" maxLength="11" className={inputCls} /></div>
                <div><label className={labelCls}>Branch</label><input type="text" name="branch" value={formData.branch} onChange={handleChange} disabled={loading} placeholder="Branch Name" className={inputCls} /></div>
              </div>

              {/* ── Remarks ── */}
              <SectionLabel emoji="📝" title="Remarks" />
              <div className="mb-6">
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} disabled={loading} rows={3} placeholder="Additional notes or remarks"
                  className={`${inputCls} resize-y font-[inherit] w-full`} />
              </div>

              {/* Form actions */}
              <div className="flex gap-3 pt-5 border-t border-gray-200">
                <button onClick={handleSave} disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {loading ? '⏳ Saving...' : editingId ? '💾 Update' : '💾 Save'}
                </button>
                <button onClick={handleCancel} disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  ❌ Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── LIST ── */}
        {!showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* List header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-white">
              <h3 className="text-[15px] font-bold text-gray-700 m-0">
                📋 Suppliers List ({filteredSuppliers.length})
              </h3>
              <div className="flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
                focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] transition-all duration-200">
                <Search size={15} className="text-gray-400 flex-shrink-0" />
                <input type="text" placeholder="Search suppliers..."
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="border-0 outline-none py-2 text-sm text-gray-800 bg-transparent font-medium w-[220px] placeholder:text-gray-400 placeholder:font-normal" />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')}
                    className="p-0.5 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500">
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                    {['Code', 'Supplier Name', 'Type', 'Contact', 'Location', 'Tax No', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                        style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && filteredSuppliers.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-12 text-gray-400">Loading...</td></tr>
                  ) : filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-12 text-gray-400">
                        {searchTerm ? 'No suppliers found matching your search.' : 'No suppliers found. Click "New Supplier" to create one.'}
                      </td>
                    </tr>
                  ) : (
                    filteredSuppliers.map(supplier => (
                      <tr key={supplier.supplierid}
                        className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                        <td className="px-4 py-3.5">
                          <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                            {supplier.suppliercode}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="font-semibold text-gray-800">{supplier.suppliername}</div>
                          {supplier.contactperson && (
                            <div className="text-[12px] text-gray-500 mt-0.5">{supplier.contactperson}</div>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          {supplier.suppliertype ? (
                            <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-lg text-[11px] font-bold">
                              {supplier.suppliertype.replace(/_/g, ' ')}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col gap-1">
                            {supplier.phone && (
                              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Phone size={12} className="text-gray-400 flex-shrink-0" />{supplier.phone}
                              </div>
                            )}
                            {supplier.email && (
                              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Mail size={12} className="text-gray-400 flex-shrink-0" />{supplier.email}
                              </div>
                            )}
                            {!supplier.phone && !supplier.email && '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="text-sm text-gray-700">
                            {supplier.cityname  && <div>{supplier.cityname}</div>}
                            {supplier.statename && <div className="text-[12px] text-gray-500">{supplier.statename}</div>}
                            {supplier.countryname && <div className="text-[11px] text-gray-400">{supplier.countryname}</div>}
                            {!supplier.cityname && !supplier.statename && !supplier.countryname && '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 font-mono text-sm text-gray-600">{supplier.gstin || '-'}</td>
                        <td className="px-4 py-3.5">
                          <div className="flex gap-1.5">
                            <button onClick={() => handleEdit(supplier)} title="Edit" disabled={loading}
                              className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 disabled:opacity-60">
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => handleDelete(supplier.supplierid)} title="Delete" disabled={loading}
                              className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 disabled:opacity-60">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Suppliers;